import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  runTransaction,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { IGroup } from '../../types/organisation';
import { ProjectManager } from '../ProjectManager';
import { isDemoMode } from '../../utilities/demoMode';
import { DemoDataManager } from '../../services/demoDataManager';
import { v4 as uuidv4 } from 'uuid';

/**
 * Create a new group and link members in a single batch write.
 */
export async function createGroup(
  orgId: string,
  data: { name: string; memberIds: string[] }
): Promise<IGroup> {
  try {
    const groupRef = doc(db, 'groups', uuidv4());
    const groupData: IGroup = {
      id: groupRef.id,
      name: data.name,
      organisationId: orgId,
      memberIds: data.memberIds,
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
    };

    const batch = writeBatch(db);
    batch.set(groupRef, groupData);

    for (const memberId of data.memberIds) {
      batch.update(doc(db, 'Users', memberId), {
        [`Organisations.${orgId}.groupIds`]: arrayUnion(groupRef.id),
        groupIds: arrayUnion(groupRef.id),
      });
    }

    await batch.commit();

    return groupData;
  } catch (error) {
    console.error('Error creating group:', error);
    throw error;
  }
}

/**
 * Get all groups for an organisation
 */
export async function getOrganisationGroups(orgId: string): Promise<IGroup[]> {
  try {
    // Check if in demo mode and organisational tutorial is active
    if (isDemoMode()) {
      const tutorialFlow = localStorage.getItem('demo_tutorial_flow');
      if (tutorialFlow === 'organisational-projects-tutorial') {
        const demoOrg = DemoDataManager.getDemoOrganisation();
        if (demoOrg && demoOrg.id === orgId) {
          return DemoDataManager.getDemoGroups();
        }
      }
    }

    const groupsRef = collection(db, 'groups');
    const q = query(groupsRef, where('organisationId', '==', orgId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => doc.data() as IGroup);
  } catch (error) {
    console.error('Error getting organisation groups:', error);
    throw error;
  }
}

/**
 * Update group members atomically (group doc + user groupIds).
 * Project access sync runs after the transaction as a best-effort follow-up.
 */
export async function updateGroupMembers(
  orgId: string,
  groupId: string,
  memberIds: string[]
): Promise<boolean> {
  try {
    const groupRef = doc(db, 'groups', groupId);
    let addedMemberIds: string[] = [];
    let removedMemberIds: string[] = [];

    await runTransaction(db, async (transaction) => {
      const groupDoc = await transaction.get(groupRef);

      if (!groupDoc.exists()) {
        throw new Error('Group not found');
      }

      const oldMemberIds: string[] = groupDoc.data().memberIds || [];
      removedMemberIds = oldMemberIds.filter((id: string) => !memberIds.includes(id));
      addedMemberIds = memberIds.filter((id: string) => !oldMemberIds.includes(id));

      transaction.update(groupRef, {
        memberIds,
        updatedAt: serverTimestamp(),
      });

      for (const memberId of removedMemberIds) {
        transaction.update(doc(db, 'Users', memberId), {
          [`Organisations.${orgId}.groupIds`]: arrayRemove(groupId),
          groupIds: arrayRemove(groupId),
        });
      }

      for (const memberId of addedMemberIds) {
        transaction.update(doc(db, 'Users', memberId), {
          [`Organisations.${orgId}.groupIds`]: arrayUnion(groupId),
          groupIds: arrayUnion(groupId),
        });
      }
    });

    // Sync project access for the group membership changes (outside transaction)
    await ProjectManager.syncGroupMembershipChanges(groupId, addedMemberIds, removedMemberIds);

    return true;
  } catch (error) {
    console.error('Error updating group members:', error);
    throw error;
  }
}

/**
 * Delete a group and strip its id from all members in one batch.
 */
export async function deleteGroup(organisationId: string, groupId: string): Promise<void> {
  const groupRef = doc(db, 'groups', groupId);
  const groupDoc = await getDoc(groupRef);

  if (!groupDoc.exists()) {
    return;
  }

  const memberIds: string[] = groupDoc.data().memberIds || [];
  const batch = writeBatch(db);

  batch.delete(groupRef);

  for (const memberId of memberIds) {
    batch.update(doc(db, 'Users', memberId), {
      [`Organisations.${organisationId}.groupIds`]: arrayRemove(groupId),
      groupIds: arrayRemove(groupId),
    });
  }

  await batch.commit();
}
